import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import type { CommunityIntegration } from "@atrium/shared";
import { api, errorMessage } from "../../../lib/api";
import { groupedPermissions, permissionsIn } from "../../../lib/permissions";
import { chatTimestamp, timeAgo } from "../../../lib/format";
import { Avatar, CommunityIcon } from "../../../components/ui/Avatar";
import { Button } from "../../../components/ui/Button";
import { EmptyState, ErrorState } from "../../../components/ui/Controls";
import { confirm } from "../../../components/ui/Dialog";
import { SettingsGroup } from "../../../components/ui/Settings";
import { RowSkeleton } from "../../../components/ui/Skeleton";
import { toast } from "../../../components/ui/Toast";
import { Tooltip } from "../../../components/ui/Tooltip";
import { BotTag } from "../../../components/UserBadges";
import { IconChevronDown, IconSlashCommand, IconTrash, IconWidget } from "../../../components/icons";
import { cn } from "../../../lib/cn";
import { permsOf } from "../hooks";
import { useRefreshCommunity, type SectionProps } from "./shared";

export const integrationsKey = (communityId: string) => ["integrations", communityId] as const;

/** Apps whose bots are in this community. Apps are added through their own install links. */
export function IntegrationsSection({ structure, go }: SectionProps) {
  const c = structure.community;
  const qc = useQueryClient();
  const refresh = useRefreshCommunity(c.id);
  const canEditRoles = permsOf(c.myPermissions, c.isOwner).manageRoles;
  const q = useQuery({
    queryKey: integrationsKey(c.id),
    queryFn: () => api.get<{ items: CommunityIntegration[] }>(`/api/communities/${c.id}/integrations`).then((r) => r.items),
  });

  const remove = async (i: CommunityIntegration) => {
    const ok = await confirm({
      title: `Remove ${i.application.name}?`,
      description: "To add it back, someone with Manage community has to use the app's install link again.",
      consequences: [
        `${i.bot.displayName} leaves ${c.name} and loses its role.`,
        i.commands.length ? `Its ${i.commands.length === 1 ? "command stops" : `${i.commands.length} commands stop`} working here.` : "It stops receiving events from this community.",
        "Messages it already sent stay.",
      ],
      confirmLabel: "Remove app",
      danger: true,
      icon: IconTrash,
    });
    if (!ok) return;
    qc.setQueryData<CommunityIntegration[]>(integrationsKey(c.id), (old) => old?.filter((x) => x.application.id !== i.application.id));
    try {
      await api.del(`/api/communities/${c.id}/integrations/${i.application.id}`);
      toast.show(`Removed ${i.application.name}`);
    } catch (e) {
      toast.error(errorMessage(e, "Couldn't remove the app"));
    } finally {
      void qc.invalidateQueries({ queryKey: integrationsKey(c.id) });
      void qc.invalidateQueries({ queryKey: ["members", c.id] });
      void qc.invalidateQueries({ queryKey: ["commands", c.id] });
      void refresh();
    }
  };

  if (q.isError) return <ErrorState message={errorMessage(q.error, "Couldn't load integrations")} onRetry={() => void q.refetch()} />;
  if (q.isPending)
    return (
      <SettingsGroup>
        <RowSkeleton />
        <RowSkeleton />
      </SettingsGroup>
    );
  if (!q.data.length)
    return (
      <EmptyState
        icon={IconWidget}
        title="No apps yet"
        description="Apps and bots are added through an app's install link. Anyone with Manage community can add one, choosing exactly which permissions it gets."
        className="!py-10"
      />
    );
  return (
    <SettingsGroup title={`${q.data.length} ${q.data.length === 1 ? "app" : "apps"}`}>
      {q.data.map((i) => (
        <IntegrationRow key={i.application.id} i={i} onRemove={() => void remove(i)} onOpenRole={i.roleId && canEditRoles ? () => go("roles", i.roleId!) : undefined} />
      ))}
    </SettingsGroup>
  );
}

function IntegrationRow({ i, onRemove, onOpenRole }: { i: CommunityIntegration; onRemove: () => void; onOpenRole?: () => void }) {
  const [open, setOpen] = useState(false);
  const perms = permissionsIn(i.permissions);
  const id = `integration-${i.application.id}`;
  return (
    <div className="px-4 py-3">
      <div className="flex items-start gap-3">
        <CommunityIcon src={i.application.iconUrl} name={i.application.name} size="md" />
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-x-2 gap-y-0.5">
            <span className="truncate text-sm font-semibold text-fg">{i.application.name}</span>
            <span className="flex min-w-0 items-center gap-1.5 text-xs text-fg-3">
              <Avatar src={i.bot.avatarUrl} name={i.bot.displayName} size="xs" className="!size-4" />
              <span className="truncate">@{i.bot.username}</span>
              <BotTag />
            </span>
          </div>
          {i.application.description && <p className="mt-0.5 line-clamp-2 text-sm text-fg-2">{i.application.description}</p>}
          <div className="mt-1 text-xs text-fg-3">
            {i.installedBy ? `Added by @${i.installedBy.username}` : "Added"}{" "}
            <Tooltip label={chatTimestamp(i.installedAt)}>
              <time dateTime={i.installedAt}>{timeAgo(i.installedAt)}</time>
            </Tooltip>
          </div>
          {i.commands.length > 0 && (
            <ul className="mt-2 flex flex-wrap gap-1" aria-label="Commands">
              {i.commands.map((c) => (
                <li key={c.name}>
                  <Tooltip label={c.description}>
                    <span className="inline-flex h-6 items-center gap-1 rounded bg-surface px-1.5 font-mono text-xs text-fg-2" tabIndex={0}>
                      <IconSlashCommand className="icon size-3.5 text-fg-3" aria-hidden />
                      {c.name}
                    </span>
                  </Tooltip>
                </li>
              ))}
            </ul>
          )}
          <button
            onClick={() => setOpen((o) => !o)}
            aria-expanded={open}
            aria-controls={id}
            className="mt-2 inline-flex items-center gap-1 rounded text-xs font-medium text-fg-2 hover:text-fg"
          >
            {perms.length === 0 ? "No extra permissions" : `${perms.length} ${perms.length === 1 ? "permission" : "permissions"}`}
            {perms.length > 0 && <IconChevronDown className={cn("icon size-4 transition-transform", open && "rotate-180")} aria-hidden />}
          </button>
          {open && perms.length > 0 && (
            <div id={id} className="mt-2 space-y-2 rounded-md bg-surface px-3 py-2">
              {groupedPermissions(i.permissions).map((g) => (
                <div key={g.group}>
                  <div className="text-xs font-medium text-fg-3">{g.title}</div>
                  <div className="text-sm text-fg-2">{g.items.map((p) => p.label).join(", ")}</div>
                </div>
              ))}
              {onOpenRole && (
                <button onClick={onOpenRole} className="text-xs font-medium text-accent hover:underline">
                  Edit in Roles
                </button>
              )}
            </div>
          )}
        </div>
        <Button size="sm" variant="ghost" icon={IconTrash} className="text-danger hover:bg-danger-soft hover:text-danger" onClick={onRemove}>
          Remove
        </Button>
      </div>
    </div>
  );
}
